import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { formatBytes, preventSubmittingByEnter } from '../../../helpers';
import DatePicker from '../../common/DatePicker';
import TextArea from '../../common/TextArea';
import moment from 'moment';
import { editCurrentSupply, setCurrentSupplyUnsaved } from '../../../actions/supplies';
import { useLocation, useParams } from 'react-router-dom';
import { fetchGetDocuments } from '../../../actions/documents';
import { toast } from 'react-toastify';
import { renderStatusSpan } from '../../Documents';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled('form')`
  padding-left: 0;
  padding-right: 0;

  .status-label {
    span {
      position: relative;
      display: inline-block;
      font-size: 10px;
      line-height: 1;
      padding: 0.25em 0.4em;
      border-radius: 0.25rem;
      font-weight: bold;
      text-align: center;
    }

    .color-new {
      background: rgb(108, 117, 125);
      color: rgb(255, 255, 255);
    }

    .color-signed-performer {
      background: rgb(79, 198, 225);
      color: rgb(255, 255, 255);
    }

    .color-signed-client,
    .color-approved-client,
    .color-approved-auto {
      background: rgb(26, 188, 156);
      color: rgb(255, 255, 255);
    }

    .color-not-approved {
      background: rgb(241, 85, 108);
      color: rgb(255, 255, 255);
    }

    .color-not-present {
      background: rgb(247, 184, 75);
      color: rgb(255, 255, 255);
    }
  }

  > div,
  > p {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    // padding-bottom: 10px;
  }

  .files-block {
    > div {
      display: flex;
      align-items: center;
      margin-top: 8px;

      &:first-child {
        margin-top: 0;
      }

      .file-name {
        display: block;
        max-width: 50%;
        color: #4fc6e1;
        font-size: 13px;
        text-decoration: underline;
        margin-right: 10px;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      .dashed-line {
        flex: 1;
        border-top: 1px dashed #eceef0;
        margin-right: 10px;
      }

      .fe-printer {
        display: block;
        color: #98a6ad;
        font-size: 15px;
        margin-right: 5px;
      }

      .file-size {
        color: #98a6ad;
        font-size: 11px;
      }
    }
  }

  .doc-info-tooltip {
    top: 5px;
  }

  .copy-icon {
    color: #7e57c2;
    font-size: 14px;
  }

  .copy-code {
    font-family: monospace;
  }

  @media (min-width: 1477px) {
    .doc-info-tooltip {
      left: -550px;
    }

    .file-title {
      width: 140px;
    }
  }
`;

const OtherBlock = forwardRef((props, ref) => {
  const { isNew } = props;

  const dispatch = useDispatch();
  const currentSupply = useSelector((state) => state.supplies.currentSupply);
  const docs = useSelector((state) => state.documents.fetchedDocs?.docs);

  const { t } = useAutoTranslation();

  const [isCopied, setIsCopied] = useState(false);

  const dateOfDeliveryPlan =
    currentSupply && currentSupply.dateOfDeliveryPlan
      ? moment(currentSupply.dateOfDeliveryPlan).valueOf()
      : null;
  const comment = currentSupply && currentSupply.comment ? currentSupply.comment : '';
  let { id } = useParams();

  const formik = useFormik({
    initialValues: {
      dateOfDeliveryPlan: null,
      comment: '',
    },

    onSubmit: (values) => {
      dispatch(
        editCurrentSupply({
          ...currentSupply,
          dateOfDeliveryPlan: values.dateOfDeliveryPlan
            ? moment(values.dateOfDeliveryPlan).format('YYYY-MM-DD HH:mm:ss')
            : null,
          comment: values.comment,
        }),
      );
    },
  });
  const { values, errors, handleSubmit, setFieldValue, submitForm } = formik;

  useEffect(() => {
    formik.setValues({ dateOfDeliveryPlan, comment });
  }, [dateOfDeliveryPlan, comment]);

  useImperativeHandle(ref, () => ({
    submitOtherBlock() {
      return submitForm();
    },
  }));

  const fulfilmentFiles = currentSupply?.files?.filter((file) => file.fileSource === `FULFILLMENT`);

  const setFieldVal = (...passArgs) => {
    dispatch(setCurrentSupplyUnsaved(true));
    return setFieldValue(...passArgs);
  };

  useEffect(() => {
    dispatch(fetchGetDocuments({ id }));
  }, []);

  const handleCopy = async (textToCopy) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
      toast.info('Сертификат скопирован');
    } catch (err) {
      console.error('Ошибка при копировании: ', err);
    }
  };

  return (
    <Wrapper className="card-box" onKeyDown={preventSubmittingByEnter} onSubmit={handleSubmit}>
      <div className="mb-2">
        <h4 className="header-title">{t('Прочее')}</h4>
        {currentSupply && currentSupply.purchaseArrivalDate && (
          <p>
            <strong>{t('Фактическая дата прихода поставки на склад')}:</strong> <br />
            {moment(currentSupply?.purchaseArrivalDate).format(`DD.MM.YYYY HH:mm:ss`)}
          </p>
        )}
        <label className="mr-1">{t('Планируемая дата поставки')}</label>
        <DatePicker
          selected={values.dateOfDeliveryPlan}
          onChange={(date) => setFieldVal('dateOfDeliveryPlan', date)}
          dateFormat="dd MMM yyyy"
        />
      </div>

      <div className="border-top pt-2 mb-2">
        <TextArea
          label={t('Комментарий складу')}
          id="comment"
          value={values.comment}
          error={errors.comment}
          onChange={(e) => setFieldVal('comment', e.target.value)}
        />
      </div>

      {!isNew ? (
        <>
          <div className="border-top" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="block-subtitle">{t('Файлы')}</div>
          </div>
          {docs?.length ? (
            <div className="files-block">
              {docs?.map((f) => (
                <div key={`file-${f.humanId}`} className="mb-3">
                  <a
                    className="file-name file-title"
                    href={f.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >{`${f.title}`}</a>
                  <div className="dashed-line" />
                  {f.size ? <div className="file-size mr-2">{formatBytes(f.size)}</div> : null}
                  <div className="mr-1">
                    {f.statusText ? (
                      <div className="status-label">{renderStatusSpan(f)}</div>
                    ) : null}
                    {f.signClientDate ? (
                      <div className="file-size">
                        {t('Дата подписи заказчика')}:{' '}
                        {moment(f.signClientDate).format('DD.MM.YYYY')}
                      </div>
                    ) : null}
                    {f.signPerformerDate ? (
                      <div className="file-size">
                        {t('Дата подписи исполнителя')}:{' '}
                        {moment(f.signPerformerDate).format('DD.MM.YYYY')}
                      </div>
                    ) : null}
                  </div>

                  <span className="help-tooltip-wrapper">
                    <i className="fe-help-circle" />
                    <span className="help-tooltip doc-info-tooltip">
                      {f.signClientCertificateInfo ? (
                        <div className="mb-1">
                          {t('Сертификат заказчика')}:
                          <i
                            className="fe-copy copy-icon ml-1"
                            title={t('Скопировать код')}
                            onClick={() => handleCopy(f.signClientCertificateInfo)}
                          />
                          <div className="copy-code">{f.signClientCertificateInfo}</div>
                        </div>
                      ) : null}
                      {f.signPerformerCertificateInfo ? (
                        <div>
                          {t('Сертификат исполнителя')}:
                          <i
                            className="fe-copy copy-icon ml-1"
                            title={t('Скопировать код')}
                            onClick={() => handleCopy(f.signPerformerCertificateInfo)}
                          />
                          <div className="copy-code">{f.signPerformerCertificateInfo}</div>
                        </div>
                      ) : null}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div>{t('Список пуст')}</div>
          )}

          {/* New version */}
          {/* {fetchedDocs?.docs?.length ? (
            <>
              <div className="border-top" style={{ display: 'flex', alignItems: 'center' }}>
                <div className="block-subtitle">Файлы складских документов</div>
              </div>
              <div className="files-block">
                {fetchedDocs?.docs?.map((item) => {
                  return (
                    <div
                      style={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        rowGap: '8px',
                        marginBottom: '16px',
                      }}
                    >
                      <strong>{item.warehouseType}</strong>
                      <a href={item.url} target="_blank">
                        <span>{item.title}</span>
                      </a>
                      <div className="status-label">{renderStatusSpan(item)}</div>
                      {item.signPerformerLink && (
                        <a href={item.signPerformerLink} target="_blank">
                          <span>Данные подписи Исполнителя</span>
                        </a>
                      )}
                      {item.signClientLink && (
                        <a href={item.signClientLink} target="_blank">
                          <span>Данные подписи Заказчика</span>
                        </a>
                      )}
                      <SignsBlock item={item} />
                    </div>
                  );
                })}
              </div>
            </>
          ) : null} */}

          {/* Old version */}
          {fulfilmentFiles?.length ? (
            <>
              <div className="border-top" style={{ display: 'flex', alignItems: 'center' }}>
                <div className="block-subtitle">{t('Файлы с фулфилмент')}</div>
              </div>
              <div className="files-block mt-1">
                {fulfilmentFiles?.map((f) => (
                  <div key={`file-${f.id}`}>
                    <a
                      className="file-name"
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >{`${f.fileName}`}</a>
                    <div className="dashed-line" />
                    {f.printWhenPacking ? <i className="fe-printer" /> : null}
                    {f.fileSize ? <div className="file-size">{formatBytes(f.fileSize)}</div> : null}
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </Wrapper>
  );
});

export default OtherBlock;
